import { Injectable } from '@angular/core';

import { select, Store, Action } from '@ngrx/store';
import { take } from 'rxjs/operators';

import * as AuthenticationActions from './authentication.actions';
import * as AuthenticationSelectors from './authentication.selectors';
import { CookieList } from '@beneficity/authentication/types';

@Injectable()
export class AuthenticationFacade {
  isAuthenticated$ = this.store.pipe(
    select(AuthenticationSelectors.getIsAuthenticated)
  );
  authError$ = this.store.pipe(select(AuthenticationSelectors.getAuthError));
  user$ = this.store.pipe(select(AuthenticationSelectors.getAuthUser));
  origin$ = this.store.pipe(select(AuthenticationSelectors.getAuthOrigin));
  cookies$ = this.store.pipe(select(AuthenticationSelectors.getCookies));
  getMbrClientNumber$ = this.store.pipe(
    select(AuthenticationSelectors.getMbrClientNumber)
  );

  accessToken$ = this.store.pipe(
    select(AuthenticationSelectors.getAccessToken)
  );
  oamRefreshAttempted$ = this.store.pipe(
    select(AuthenticationSelectors.getOamRefreshAttempted)
  );

  logoutAttempted$ = this.store.pipe(
    select(AuthenticationSelectors.getLogoutAttempted)
  );
  isSalesDemo$ = this.store.pipe(
    select(AuthenticationSelectors.getIsSalesDemo)
  );

  getSearchType$ = this.store.pipe(
    select(AuthenticationSelectors.getSearchType)
  );

  getUbk(): number {
    let a = null;
    this.user$.pipe(take(1)).subscribe((y) => (a = y?.ubk));
    return a;
  }

  getCookieList(): CookieList {
    let a = null;
    this.cookies$.pipe(take(1)).subscribe((y) => (a = y));
    return a;
  }

  getAuthDomain(): string {
    let a = null;
    this.origin$.pipe(take(1)).subscribe((y) => (a = y));
    return a;
  }

  getMbrClientNumber(): string {
    let a = null;
    this.getMbrClientNumber$.pipe(take(1)).subscribe((y) => (a = y));
    return a;
  }

  getAccessToken(): string {
    let a = null;
    this.accessToken$.pipe(take(1)).subscribe((y) => (a = y));
    return a;
  }

  /**
   * Checks if the current user is a Wyoming (BCBS Wyoming) client
   * based on the scope in the authentication token
   * @returns true if user has Wyoming scope
   */
  isWyomingClient(): boolean {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return false;
    }

    try {
      const decodedToken = this.decodeToken(accessToken);

      const scope = decodedToken?.scope || '';
      return (
        scope.includes('engen.health-plan.bcbswy') &&
        !scope.includes('customer-service.read-only')
      );
    } catch (error) {
      console.error('Error checking Wyoming client scope:', error);
      return false;
    }
  }

  /**
   * Decodes JWT token to extract payload
   * @param token - JWT access token
   * @returns Decoded token payload
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  constructor(private store: Store) {}

  private dispatch(action: Action) {
    this.store.dispatch(action);
  }

  authenticate(
    accessToken: string,
    origin: string,
    defaultOverrideValues?: Record<string, string>
  ): void {
    this.dispatch(
      AuthenticationActions.authenticate({
        accessToken,
        origin,
        defaultOverrideValues,
      })
    );
  }

  logout(domain: string): void {
    this.dispatch(AuthenticationActions.logout({ domain }));
  }

  updateCookies(accessToken: string, cookies: Record<string, string>) {
    this.dispatch(
      AuthenticationActions.updateCookies({ accessToken, cookies })
    );
  }

  public oamRefresh() {
    this.store.dispatch(AuthenticationActions.oamSessionRefresh());
  }
}
